import React from 'react'
import { Alert, Box, Button, Paper, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material'
import FileUploadIcon from '@mui/icons-material/DriveFolderUpload'
import { approvalBooleanToString, approvalStringToBoolean } from 'logic/helpers'
import Header from 'components/Header'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import AdminNurseDetailContext from 'pages/AdminNurseDetail/context'
import WorkReference from 'logic/WorkReference'
import DescriptionList from 'components/DescriptionList'
import { useFirebase } from 'contexts/FirebaseProvider'
import { v4 as uuidv4 } from 'uuid'
import { ref, uploadBytes, uploadString, StringFormat, getDownloadURL } from "firebase/storage"

type Status = 'not-set' | 'approved' | 'disapproved'

const AdminNurseWorkReferenceVerify: React.FC = () => {
    const { nurse, fetchNurse } = React.useContext(AdminNurseDetailContext)
    const { api } = useAPI()
    const { storage } = useFirebase()
    const { showAlert } = useSnackBarAlert()

    const [workReferences, setWorkReferences] = React.useState<WorkReference[]>([])
    const [referenceFileURL, setReferenceFileURL] = React.useState<string>()
    const [status, setStatus] = React.useState<Status>('not-set')
    const [loading, setLoading] = React.useState<boolean>(false)

    const fetchWorkReferences = React.useCallback(async () => {
        const workReferences = await api.listWorkReferences({ nurse_id: nurse.nurse_id, sideloads: ['nurse'] })
        setWorkReferences(workReferences)
    }, [api, nurse.nurse_id])

    const fetchReferenceFileURL = React.useCallback(async () => {
        if (!nurse.reference_file) return
        const fileRef = ref(storage, `work-reference/${nurse.nurse_id}/${nurse.reference_file}`)
        const url = await getDownloadURL(fileRef)
        setReferenceFileURL(url)
    }, [nurse.nurse_id, nurse.reference_file, storage])

    const sendWorkReferenceRequest = React.useCallback(async () => {
        try {
            await api.sendWorkReferenceRequest(nurse.nurse_id)
            showAlert('success', 'Work reference request sent')
        } catch (error) {
            showAlert('error', 'Failed to send work reference request')
        }
    }, [api, nurse.nurse_id, showAlert])

    const updateWorkReferenceStatus = React.useCallback(async () => {
        try {
            setLoading(true)
            const referenceApproved = approvalStringToBoolean(status)
            await api.editNurse({ reference_approved: referenceApproved }, nurse.nurse_id)
            await fetchNurse()
        } catch (error) {
            console.error(error)
            showAlert('error', 'Failed to update work reference status')
        } finally {
            setLoading(false)
        }
    }, [api, fetchNurse, nurse.nurse_id, status, showAlert])

    const uploadWorkReferenceFile = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        try {
            setLoading(true)
            const key = uuidv4()
            const fileRef = ref(storage, `work-reference/${nurse.nurse_id}/${key}`)
            const metadata = {
                contentType: file.type,
                cacheControl: 'no-store',
            }
            if (typeof file === 'string') {
                await uploadString(fileRef, file, StringFormat.DATA_URL, metadata)
            } else {
                await uploadBytes(fileRef, file, metadata)
            }
            await api.editNurse({ reference_file: key, reference_file_content_type: file.type }, nurse.nurse_id)
            await fetchNurse()
            showAlert('success', 'Work reference file uploaded')
        } catch (e: any) {
            console.error(e)
            showAlert('error', 'Failed to upload work reference file')
        } finally {
            setLoading(false)
        }
    }, [api, fetchNurse, nurse.nurse_id, showAlert, storage])

    React.useEffect(() => {
        fetchWorkReferences()
    }, [fetchWorkReferences])

    React.useEffect(() => {
        setStatus(approvalBooleanToString(nurse.reference_approved))
    }, [nurse.reference_approved])

    React.useEffect(() => {
        fetchReferenceFileURL()
    }, [fetchReferenceFileURL])

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gridTemplateAreas: `'presentation form'`,
            gap: 2
        }}>
            <Box sx={{ gridArea: 'presentation', alignSelf: 'center' }}>
                {workReferences.map((workReference) => (
                    <Paper key={workReference.id} sx={{ padding: 2 }}>
                        <DescriptionList labelWidth='300px' data={[{ 
                            label: 'Reference From', 
                            value: workReference.reference_name 
                            }, { 
                            label: 'Patient Communication', 
                            value: workReference.patient_communication 
                            }, { 
                            label: 'Scaling and Polishing', 
                            value: workReference.scaling_and_polishing 
                            }, { 
                            label: 'Infection Control Procedures', 
                            value: workReference.infection_control_procedures 
                            }, { 
                            label: 'Record Keeping and Documentation', 
                            value: workReference.record_keeping_and_documentation 
                            }, { 
                            label: 'Time Management', 
                            value: workReference.time_management 
                            }, { 
                            label: 'Teamwork and Collaboration', 
                            value: workReference.teamwork_and_collaboration 
                            }, { 
                            label: 'Professionalism', 
                            value: workReference.professionalism 
                            }, { 
                            label: 'Comments', 
                            value: workReference.comments,
                            sx: { whiteSpace: 'pre-line' }
                            }, {
                            label: 'Created At',
                            value: workReference.created_at.toFormat('LLL dd, yyyy - hh:mm a')
                        }]} />
                    </Paper>
                ))}
                {/* Display the uploaded reference file (if any) */}
                {referenceFileURL ? (
                    nurse.reference_file_content_type === 'application/pdf' ? (
                        <Box component='embed' src={referenceFileURL} sx={{ width: '100%', height: '75vh' }} />
                    ) : (
                        <Box component='img' src={referenceFileURL} sx={{ width: '100%' }} />
                    )
                ) : (
                    <Alert severity='warning' variant='filled'>
                        No reference provided yet.
                    </Alert>
                )}
            </Box>
            <Box sx={{ gridArea: 'form', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Header variant='h3' text='Work Reference (Required for Hygienist)'></Header>
                <RadioGroup row
                    value={status}
                    onChange={(e, value) => setStatus(value as Status)}>
                    <FormControlLabel value='not-set' control={<Radio color='warning' />} label="Unset" />
                    <FormControlLabel value='approved' control={<Radio color='success' />} label="Approve" />
                    <FormControlLabel value='disapproved' control={<Radio color='error' />} label="Disapprove" />
                </RadioGroup>
                <Button variant="contained" color="primary" onClick={updateWorkReferenceStatus} disabled={loading}>
                    {loading ? 'Please Wait ...' : 'Update Work Reference Status'}
                </Button>
                <Button
                    variant="outlined"
                    endIcon={<FileUploadIcon />}
                    component="label">
                    Upload File
                    <input
                        hidden
                        id='work-reference-file-upload'
                        accept='image/jpeg, image/png, application/pdf'
                        type="file"
                        onChange={uploadWorkReferenceFile} />
                </Button>
                <Paper sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant='body1'>
                        Name: {nurse?.reference_from_name || 'Not Provided'} <br />
                        Email: {nurse?.reference_from_email || 'Not Provided'} <br />
                        Requested at: {nurse?.reference_requested_at?.toFormat('dd/MM/yyyy hh:mm a') || 'Not Requested Yet'} <br />
                        Received at: {nurse?.reference_received_at?.toFormat('dd/MM/yyyy hh:mm a') || 'Not Received Yet'} <br />
                    </Typography>
                    <Button variant='contained' color='primary' onClick={sendWorkReferenceRequest}>
                        { nurse?.reference_requested_at ? 'Resend' : 'Send' } Reference Request Email
                    </Button>
                </Paper>
            </Box>
        </Box>
    )
}

export default AdminNurseWorkReferenceVerify